import { DateRangePickerWrapper } from 'components/Date/DateRangePicker';
import { Loading } from 'components/loading/Loading';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchHistoryForInventoryItem } from 'services/api/inventoryItems';
import { DATE_FORMATS, INVENTORY_HISTORY_STATUS_TYPE, INVENTORY_QUANTITY_UPDATE_REASON, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import styles from './index.module.scss';
import moment from 'moment';
import ActionButton from 'components/ActionButton';
import ItemHistoryChart from './ItemHistoryChart';
import CustomLink from 'components/TableLink';
import _ from 'lodash';
import DropDown from 'components/FormFields/dropdown';

let filters: {
  [p: string]: any;
  page: number;
  startDate: string | null;
  endDate: string | null;
} = {
  page: 0,
  limit: getPageLimit(),
  startDate: moment().subtract(1, 'week').toString(),
  endDate: moment().toString(),
};

const itemSold = (log: Record<string, any>) => log.change < 0;

const renderOrderStatus = (row) => {
  let status;
  if (row?.orderId) {
    status = _.get(row, 'orderStatus', '-');
  } else if (row?.purchaseOrderId) {
    status = _.get(row, 'poStatus', '-');
  } else {
    status = '-';
  }

  return (
    <div>
      {status}
    </div>);
};

const ItemHistoryLogs = props => {
  const [historyLogs, setHistoryLogs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string | null>(filters.startDate);
  const [reason, setReason] = useState<string>('');
  const [endDate, setEndDate] = useState<string | null>(filters.endDate);
  const { itemId, showItemHistoryChart = true, isUnfulfilledQuantityChangeTable = true } = props;
  const historyData = {
    ...filters,
    id: itemId,
    startDate: formatDate(filters.startDate, DATE_FORMATS.YYYY_MM_DD),
    endDate: formatDate(filters.endDate, DATE_FORMATS.YYYY_MM_DD),

  };
  const fetchInventoryItemQuantityHistoryFn = async () => {
    setLoading(true);
    try {
      let response: Record<string, any>;
      if (isUnfulfilledQuantityChangeTable) {
        response = await fetchHistoryForInventoryItem({ ...historyData, type: INVENTORY_HISTORY_STATUS_TYPE.QUANTITY_CHANGE, reason });
      } else {
        response = await fetchHistoryForInventoryItem({ ...historyData, type: INVENTORY_HISTORY_STATUS_TYPE.UNFULFILLED_QUANTITY_CHANGED, reason });
      }
      setHistoryLogs(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItemQuantityHistoryFn();
  }, []);

  const columns = [
    {
      name: 'Date Time',
      selector: 'objectId',
      width: '250px',
      format: row => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY___hh_mm_A),
    },
    {
      name: 'Action',
      selector: 'name',
      width: '235px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div className={styles['action']}>
          <div className={`${styles['action-tag']} ${styles[itemSold(row) ? 'sold' : 'added']}`}>
            {row.reason}
          </div>
        </div>
      ),
    },
    {
      name: 'Reason',
      selector: 'reasonForAdjustment',
      format: row => row.reasonForAdjustment || 'N/A',
    },
    {
      name: 'Old Unfulfilled Stock',
      selector: '',
      cell: row => row?.type === INVENTORY_HISTORY_STATUS_TYPE.UNFULFILLED_QUANTITY_CHANGED ? row?.previous : '-',
      omit: isUnfulfilledQuantityChangeTable,
    },
    {
      name: 'New Unfulfilled Stock',
      selector: '',
      cell: row => row?.type === INVENTORY_HISTORY_STATUS_TYPE.UNFULFILLED_QUANTITY_CHANGED ? row?.updated : '-',
      omit: isUnfulfilledQuantityChangeTable,
    },
    {
      name: 'Change',
      selector: 'category.description',
      sortable: true,
      format: row => (itemSold(row) ? '' : '+') + row.change,
      omit: !isUnfulfilledQuantityChangeTable,
    },
    {
      name: 'New Quantity',
      selector: 'updated',
      sortable: true,
      cell: row => row?.type === INVENTORY_HISTORY_STATUS_TYPE.UNFULFILLED_QUANTITY_CHANGED ? '-' : row?.updated,
      omit: !isUnfulfilledQuantityChangeTable,
    },
    {
      name: 'Order ID',
      selector: 'orderId',
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: row => row.orderId ? (
        <CustomLink
          text={row.orderId}
          linkTo={`/orders/${row.orderId}`}
        />
      ) : 'N/A',
    },
    {
      name: 'Purchase Order ID',
      selector: 'purchaseOrderId',
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: row => row.purchaseOrderId ? (
        <CustomLink
          text={row.purchaseOrderId}
          linkTo={`/purchase-orders/${row.purchaseOrderId}`}
        />
      ) : 'N/A',
    },
    {
      name: 'status',
      selector: 'status',
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: row => {
        return renderOrderStatus(row);
      },
    },
    {
      name: 'User',
      selector: 'user',
      cell: row => row.user ? (
        <CustomLink
          text={row?.user?.id}
          linkTo={`/users/${row?.user?.id}`}
        />
      ) : 'N/A',
    },
  ];

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    fetchInventoryItemQuantityHistoryFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  const onDateSelectionChange = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    filters = {
      ...filters,
      startDate: moment(startDate).toISOString(),
      endDate: moment(endDate).toISOString(),
    };
    setStartDate(moment(startDate).toISOString());
    setEndDate(moment(endDate).toISOString());
  };

  const SectionHeader = () => (
    <div className={`d-flex px-0 px-md-4 justify-content-between align-items-center flex-column flex-md-row`}>
      <div style={{ fontSize: `20px`, lineHeight: `26px`, color: '#161616' }} className={styles['section-heading mb-3']}>
        {`Item History ${isUnfulfilledQuantityChangeTable ? '' : 'Unfulfilled Quantity'} Log`}
      </div>
      <div className={'ml-0 ml-lg-auto'}>
        <Filter />
      </div>
    </div>
  );

  const getDataTable = () => {
    if (loading) return <Loading type={'mini'} />;
    if (historyLogs?.items?.length) {
      return (
        <DataTable
          keyField="objectid"
          columns={columns}
          noHeader={true}
          data={historyLogs?.items}
          highlightOnHover={true}
          defaultSortField="createdAt"
          defaultSortAsc={false}
          customStyles={tableStyles}
          pagination={true}
          paginationServer={true}
          paginationPerPage={getPageLimit()}
          paginationDefaultPage={(filters.page || 0) + 1}
          paginationTotalRows={historyLogs.total}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      );
    } else return <div />;
  };


  const Filter = () => (
    <div className={'d-flex flex-column flex-md-row align-items-center align-items-md-end mb-3 mt-3 mt-md-0'}>
      <DropDown containerClass='mr-2' title="Reason" value={reason} onChange={setReason} options={INVENTORY_QUANTITY_UPDATE_REASON} />
      <DateRangePickerWrapper
        startDate={startDate}
        endDate={endDate}
        onDatesChange={onDateSelectionChange}
      />
      <ActionButton
        text={'Submit'}
        active={!loading}
        isLoading={loading}
        onClick={fetchInventoryItemQuantityHistoryFn}
        customClass={'ml-3 mt-3 mt-md-0'}
      />
    </div>
  );

  return (
    <div className={styles['item-history-log-container']}>
      <SectionHeader />
      {showItemHistoryChart && <ItemHistoryChart historyData={historyData} />}
      {getDataTable()}
    </div>
  );
};

export default ItemHistoryLogs;
